import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import InstallPrompt from '@/components/InstallPrompt';
import PwaUpdateBanner from '@/components/PwaUpdateBanner';
import SaaSLandingPage from '@/pages/SaaSLandingPage';
import DoctorWebsite from '@/pages/DoctorWebsite';
import DoctorWebsiteMockA from '@/pages/DoctorWebsiteMockA';
import DoctorWebsiteMockB from '@/pages/DoctorWebsiteMockB';
import TqsMethodologyPage from '@/pages/TqsMethodologyPage';
import DoctorArticle from '@/pages/DoctorArticle';
import DoctorArticlesIndex from '@/pages/DoctorArticlesIndex';
import DoctorProcedurePage from '@/pages/DoctorProcedurePage';
import DoctorDashboard from '@/pages/DoctorDashboard';
import DoctorCredentialsPage from '@/pages/DoctorCredentialsPage';
import ErrorBoundary from '@/components/ErrorBoundary';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import BookingsPage from '@/pages/BookingsPage';
import BrochurePage from '@/pages/BrochurePage';
import AdminDashboard from '@/pages/AdminDashboard';
import UpgradePage from '@/pages/UpgradePage';
import ProductBrochure from '@/pages/ProductBrochure';
import DoctorTerms from '@/pages/DoctorTerms';
import PatientTerms from '@/pages/PatientTerms';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ContactPage from '@/pages/ContactPage';
import PatientValueProp from '@/pages/PatientValueProp';
import InviteYourDoctorPage from '@/pages/InviteYourDoctorPage';
import MyClinicsPage from '@/pages/MyClinicsPage';
import MyClinicsPreviewPage from '@/pages/MyClinicsPreviewPage';
import BusinessCasePage from '@/pages/BusinessCasePage';
import ConnectOtpLaunch from '@/pages/launch/ConnectOtpLaunch';
import DoctorNotifyLaunch from '@/pages/launch/DoctorNotifyLaunch';
import PinFastLoginLaunch from '@/pages/launch/PinFastLoginLaunch';
import VoiceAndChatLaunch from '@/pages/launch/VoiceAndChatLaunch';
import ArticlesLaunch from '@/pages/launch/ArticlesLaunch';
import CredentialedSignupLaunch from '@/pages/launch/CredentialedSignupLaunch';
import PatientLogLaunch from '@/pages/launch/PatientLogLaunch';
import PatientProtectedChatLaunch from '@/pages/launch/PatientProtectedChatLaunch';
import ClinicCheckinQrLaunch from '@/pages/launch/ClinicCheckinQrLaunch';
import PatientLogLiveDashboardLaunch from '@/pages/launch/PatientLogLiveDashboardLaunch';
import DhfV3ConditionsDpdpLaunch from '@/pages/launch/DhfV3ConditionsDpdpLaunch';
import AutoRxLaunch from '@/pages/launch/AutoRxLaunch';
import VerifiedRxLaunch from '@/pages/launch/VerifiedRxLaunch';
import SmartMedicineComposerLaunch from '@/pages/launch/SmartMedicineComposerLaunch';
import PrescriptionPlatformLaunch from '@/pages/launch/PrescriptionPlatformLaunch';
import CareProgressMonitorLaunch from '@/pages/launch/CareProgressMonitorLaunch';
import RapidSaveSameDayLaunch from '@/pages/launch/RapidSaveSameDayLaunch';
import ForgotPinPage from '@/pages/ForgotPinPage';
import ResetPinPage from '@/pages/ResetPinPage';
import DhfShareLandingPage from '@/pages/DhfShareLandingPage';
import WhatsNewIndexPage from '@/pages/WhatsNewIndexPage';
import FindDoctorsPage from '@/pages/FindDoctorsPage';
import InsightsHub from '@/pages/InsightsHub';
import ForSpecialtyPage from '@/pages/ForSpecialtyPage';
import ForDoctorsHub from '@/pages/ForDoctorsHub';
import ComparePage from '@/pages/ComparePage';
import ResourcesHub from '@/pages/ResourcesHub';
import ResourceArticlePage from '@/pages/ResourceArticlePage';
import DoctorOnboardingGuide from '@/pages/DoctorOnboardingGuide';
import VisualWalkthrough from '@/pages/VisualWalkthrough';
import GetStartedPage from '@/pages/GetStartedPage';
import PatientDataProtection from '@/pages/PatientDataProtection';
import HospitalSponsorshipPage from '@/pages/HospitalSponsorshipPage';
import HeroMockPage from '@/pages/HeroMockPage';
import TqsSurveyPage from '@/pages/TqsSurveyPage';
import PatientHealthFilePage from '@/pages/PatientHealthFilePage';
import PatientHealthFileFounderReplica from '@/pages/PatientHealthFileFounderReplica';
import DoctorConnectPage from '@/pages/DoctorConnectPage';
import HomeABTest from '@/pages/HomeABTest';
import SavedContactLanding from '@/pages/SavedContactLanding';
import ClinicCheckinPage from '@/pages/ClinicCheckinPage';
import RequestAppointmentPage from '@/pages/RequestAppointmentPage';
import PatientAppointmentRequestPage from '@/pages/PatientAppointmentRequestPage';
import RxVerifyPage from '@/pages/RxVerifyPage';
import DrugRegistryAdminPage from '@/pages/admin/DrugRegistryAdminPage';
import SurgicalDischargePage from '@/pages/SurgicalDischargePage';
import SurgicalResidentLoginPage from '@/pages/SurgicalResidentLoginPage';
import SurgicalSharedDischargePage from '@/pages/SurgicalSharedDischargePage';
import SurgicalSuiteMock from '@/pages/SurgicalSuiteMock';
import SurgicalVerifyPage from '@/pages/SurgicalVerifyPage';
import PatientRecoveryPage from '@/pages/PatientRecoveryPage';
import DhfComposerMock from '@/pages/mocks/DhfComposerMock';
import DoctorCareProgressMock from '@/pages/mocks/DoctorCareProgressMock';
import PatientCareProgressMock from '@/pages/mocks/PatientCareProgressMock';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Installed app opens at start_url "/" — send recovery patients straight back to their plan.
if (typeof window !== 'undefined' && window.location.pathname === '/'
    && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true)
    && localStorage.getItem('docsite_recovery_plan')) {
  window.location.replace(`/recovery/${localStorage.getItem('docsite_recovery_plan')}`);
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <InstallPrompt />
        <PwaUpdateBanner />
        <Routes>
          {/* Doctor's public website - no header/footer (standalone) */}
          <Route path="/dr/:slug" element={<DoctorWebsite />} />
          <Route path="/dr/:slug/hi" element={<SavedContactLanding />} />
          <Route path="/dr/:slug/checkin" element={<ClinicCheckinPage />} />
          <Route path="/dr/:slug/request-appointment" element={<RequestAppointmentPage />} />
          <Route path="/appointment-request/:id" element={<PatientAppointmentRequestPage />} />
          <Route path="/dr/:slug/preview-a" element={<DoctorWebsiteMockA />} />
          <Route path="/dr/:slug/preview-b" element={<DoctorWebsiteMockB />} />
          <Route path="/methodology/tqs" element={<TqsMethodologyPage />} />
          <Route path="/dr/:slug/articles/:articleSlug" element={<DoctorArticle />} />
          <Route path="/dr/:slug/articles" element={<DoctorArticlesIndex />} />
          <Route path="/dr/:slug/procedures/:procedureSlug" element={<DoctorProcedurePage />} />

          {/* Auto-SEO pages — standalone with own layout */}
          <Route path="/find/:specialty/:city" element={<FindDoctorsPage />} />
          <Route path="/find/:specialty" element={<FindDoctorsPage />} />

          {/* Hero A/B mock — standalone preview, no global header/footer */}
          <Route path="/hero-mock" element={<HeroMockPage />} />
          <Route path="/tqs/:token" element={<TqsSurveyPage />} />
          <Route path="/h/:token" element={<PatientHealthFilePage />} />
          <Route path="/dhf-share/:share_token" element={<DhfShareLandingPage />} />
          <Route path="/dhf-replica" element={<PatientHealthFileFounderReplica />} />
          <Route path="/c/:slug" element={<DoctorConnectPage />} />
          <Route path="/rx-verify" element={<RxVerifyPage />} />
          <Route path="/admin/drug-registry" element={<DrugRegistryAdminPage />} />
          <Route path="/surgical-suite" element={<SurgicalDischargePage />} />
          <Route path="/surgical-suite/login" element={<SurgicalResidentLoginPage />} />
          <Route path="/surgical-share/:token" element={<SurgicalSharedDischargePage />} />
          <Route path="/mock/surgical-suite" element={<SurgicalSuiteMock />} />
          <Route path="/surgical-verify/:viewToken" element={<SurgicalVerifyPage />} />
          <Route path="/recovery/:planId" element={<PatientRecoveryPage />} />

          {/* v2 concept mocks — standalone, no header/footer */}
          <Route path="/mock/dhf-composer" element={<DhfComposerMock />} />
          <Route path="/mock/doctor-care-progress" element={<DoctorCareProgressMock />} />
          <Route path="/mock/patient-care-progress" element={<PatientCareProgressMock />} />

          {/* Platform pages with header/footer */}
          <Route path="*" element={
            <div className="flex flex-col min-h-screen">
              <Header />
              <main className="flex-1">
                <Routes>
                  <Route path="/" element={<HomeABTest />} />
                  <Route path="/onboarding-guide" element={<DoctorOnboardingGuide />} />
                  <Route path="/visual-walkthrough" element={<VisualWalkthrough />} />
                  <Route path="/get-started" element={<GetStartedPage />} />
                  <Route path="/features" element={<BrochurePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/dashboard/*" element={<ErrorBoundary fallbackTitle="Dashboard hit an unexpected error"><DoctorDashboard /></ErrorBoundary>} />
                  <Route path="/dashboard-credentials" element={<ErrorBoundary fallbackTitle="Credentials page hit an unexpected error"><DoctorCredentialsPage /></ErrorBoundary>} />
                  <Route path="/bookings" element={<BookingsPage />} />
                  <Route path="/admin/*" element={<AdminDashboard />} />
                  <Route path="/upgrade" element={<UpgradePage />} />
                  <Route path="/brochure" element={<ProductBrochure />} />
                  <Route path="/terms/doctor" element={<DoctorTerms />} />
                  <Route path="/terms/patient" element={<PatientTerms />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/for-patients" element={<PatientValueProp />} />
                  <Route path="/invite-your-doctor" element={<InviteYourDoctorPage />} />
                  <Route path="/my-clinics" element={<MyClinicsPage />} />
                  <Route path="/preview/my-clinics" element={<MyClinicsPreviewPage />} />
                  <Route path="/business-case" element={<BusinessCasePage />} />
                  <Route path="/whats-new" element={<WhatsNewIndexPage />} />
                  <Route path="/whats-new/connect-otp" element={<ConnectOtpLaunch />} />
                  <Route path="/whats-new/doctor-notify" element={<DoctorNotifyLaunch />} />
                  <Route path="/whats-new/pin-fast-login" element={<PinFastLoginLaunch />} />
                  <Route path="/whats-new/voice-and-chat" element={<VoiceAndChatLaunch />} />
                  <Route path="/whats-new/articles-pages" element={<ArticlesLaunch />} />
                  <Route path="/whats-new/credentialed-signup" element={<CredentialedSignupLaunch />} />
                  <Route path="/whats-new/patient-log" element={<PatientLogLaunch />} />
                  <Route path="/whats-new/patient-protected-chat" element={<PatientProtectedChatLaunch />} />
                  <Route path="/whats-new/clinic-checkin-qr" element={<ClinicCheckinQrLaunch />} />
                  <Route path="/whats-new/patient-log-live-dashboard" element={<PatientLogLiveDashboardLaunch />} />
                  <Route path="/whats-new/dhf-v3-conditions-dpdp" element={<DhfV3ConditionsDpdpLaunch />} />
                  <Route path="/whats-new/auto-prescriptions" element={<AutoRxLaunch />} />
                  <Route path="/whats-new/docsite-verified-rx" element={<VerifiedRxLaunch />} />
                  <Route path="/whats-new/smart-medicine-composer" element={<SmartMedicineComposerLaunch />} />
                  <Route path="/whats-new/prescription-platform" element={<PrescriptionPlatformLaunch />} />
                  <Route path="/whats-new/care-progress-monitor" element={<CareProgressMonitorLaunch />} />
                  <Route path="/whats-new/rapid-save-same-day-rule" element={<RapidSaveSameDayLaunch />} />
                  <Route path="/forgot-pin" element={<ForgotPinPage />} />
                  <Route path="/reset-pin" element={<ResetPinPage />} />
                  <Route path="/insights" element={<InsightsHub />} />
                  <Route path="/for" element={<ForDoctorsHub />} />
                  <Route path="/for/:specialty" element={<ForSpecialtyPage />} />
                  <Route path="/compare/:competitor" element={<ComparePage />} />
                  <Route path="/resources" element={<ResourcesHub />} />
                  <Route path="/resources/:slug" element={<ResourceArticlePage />} />
                  <Route path="/security/patient-data" element={<PatientDataProtection />} />
                  <Route path="/for/hospitals" element={<HospitalSponsorshipPage />} />
                </Routes>
              </main>
              <Footer />
            </div>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
